import { useMemo, useState } from "react";
import { BotIcon, CheckIcon, SearchIcon } from "lucide-react";

import { useUserSheet } from "../userSheetContext";
import {
  RecommendedJobPreviewDialog,
  RecommendedJobTitlePreview,
} from "./RecommendedJobTitlePreview";

import {
  ClearableSearchInput,
  Column,
  EmptyDataDash,
  ErrorText,
  LoadingIndicator,
  Row,
  Text,
} from "@/components";
import { AgenticScoreChip } from "@jobs/pipeline/shared/AgenticScoreChip";
import { PriorityCell } from "@/components/portfolio/table/cells/PriorityCell";
import { StafferCell } from "@/components/portfolio/table/cells/StafferCell";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useCreateAddRecommendedCandidacy } from "@/hooks/createAdds/useCreateAddRecommendedCandidacy";
import { useGetCandidacyInfo } from "@/hooks/queries/useGetCandidacyInfo";
import { useGetCandidacySheetData } from "@/hooks/queries/useGetCandidacySheetData";
import { useGetCompaniesSummary } from "@/hooks/queries/useGetCompaniesSummary";
import { agenticScoreToBucket } from "@/lib/agenticScore";
import { RECOMMENDED_JOBS_LIMIT } from "@/lib/constants";
import { formatting } from "@/lib/formatting";
import { novaClient } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { JobPriority, JobStatus } from "@/types";

const SEARCH_RESULTS_LIMIT = 10;

interface RecommendedJob {
  agenticScore?: number | null;
  candidacyOwnerRotation?: { rotation: number[] } | null;
  company?: { id?: string; name?: string };
  estimatedRevenueAmount?: number | null;
  id?: string;
  priority?: JobPriority | null;
  title?: string;
}

export const RecommendedJobsTab = () => {
  /* Context */
  const { candidacyId, contentPaddingClassName, jobId, workerId } =
    useUserSheet();

  /* Database CRUD Operations */
  // Create
  const { isPending: isRecommending, mutate: recommendCandidate } =
    useCreateAddRecommendedCandidacy();
  // Read
  const { data: candidacyInfo } = useGetCandidacyInfo({
    candidacyId: candidacyId ?? "",
  });
  const sheetDataApi = useGetCandidacySheetData({ jobId, userId: workerId });
  // Exclude the current job plus any job the candidate is already actively in.
  const excludeJobIds = [
    ...(jobId ? [jobId] : []),
    ...(candidacyInfo?.activePipelines ?? []).map(({ jobId }) => jobId),
  ];
  const recommendedJobsQuery =
    novaClient.internal.jobs.recommendations.findRecommendedInternalJobsByUser.useQuery(
      {
        desiredPosition: sheetDataApi.data?.profile.desiredPosition,
        excludeJobIds,
        limit: RECOMMENDED_JOBS_LIMIT,
        userId: workerId,
      },
    );
  // All companies + jobs — powers the manual job search below.
  const companiesSummaryQuery = useGetCompaniesSummary();

  /* React Hooks */
  const [recommendingJobId, setRecommendingJobId] = useState<string | null>(
    null,
  );
  const [recommendedJobIds, setRecommendedJobIds] = useState<string[]>([]);
  const [previewJob, setPreviewJob] = useState<RecommendedJob>();
  const [searchQuery, setSearchQuery] = useState("");

  const trimmedQuery = searchQuery.trim().toLowerCase();
  const isSearching = trimmedQuery.length > 0;

  // Manual search across every open job (title or company name match) so a
  // recruiter can recommend a job the automatic list didn't surface.
  const searchedJobs = useMemo<RecommendedJob[]>(() => {
    if (!isSearching) return [];
    const excluded = new Set([
      ...(jobId ? [jobId] : []),
      ...(candidacyInfo?.activePipelines ?? []).map(({ jobId }) => jobId),
    ]);
    return (companiesSummaryQuery.data ?? [])
      .flatMap((company) => company.jobs.map((job) => ({ company, job })))
      .filter(
        ({ company, job }) =>
          job.status !== JobStatus.CLOSED &&
          !excluded.has(job.id) &&
          (job.title.toLowerCase().includes(trimmedQuery) ||
            company.name.toLowerCase().includes(trimmedQuery)),
      )
      .slice(0, SEARCH_RESULTS_LIMIT)
      .map(({ company, job }) => ({
        company: { id: company.id, name: company.name },
        id: job.id,
        title: job.title,
      }));
  }, [
    candidacyInfo?.activePipelines,
    companiesSummaryQuery.data,
    isSearching,
    jobId,
    trimmedQuery,
  ]);

  /* Helpers */
  const recommendedJobs = (recommendedJobsQuery.data ?? []) as RecommendedJob[];
  const displayedJobs = isSearching ? searchedJobs : recommendedJobs;
  const hasAgenticScore = displayedJobs.some(
    (job) => job.agenticScore !== undefined,
  );
  const columnCount = hasAgenticScore ? 6 : 5;

  /* Handlers */
  const handleRowClick = (job: RecommendedJob) => {
    if (!job.company?.id || !job.company.name || !job.id) return;
    setPreviewJob(job);
  };

  const handleRecommend = (job: RecommendedJob) => {
    const companyId = job.company?.id;
    const jobId = job.id;
    if (!companyId || !jobId) return;

    setRecommendingJobId(jobId);
    recommendCandidate(
      {
        companyId,
        jobId,
        talentId: workerId,
        sourceCandidacyId: candidacyId,
      },
      {
        onSettled: () => {
          setRecommendingJobId(null);
        },
        onSuccess: () => {
          setRecommendedJobIds((previous) => [...previous, jobId]);
        },
      },
    );
  };

  /* Render */
  const renderHeader = (label: string, className?: string) => {
    return (
      <TableHead className={cn("h-12 px-3", className)}>
        <Text className="whitespace-nowrap text-xs font-medium text-slate-500">
          {label}
        </Text>
      </TableHead>
    );
  };

  const renderRecommendCell = (job: RecommendedJob) => {
    const jobId = job.id;
    if (!jobId || !job.company?.id) return <EmptyDataDash />;

    if (recommendedJobIds.includes(jobId)) {
      return (
        <Row className="items-center gap-1.5">
          <CheckIcon className="h-4 w-4 text-green-600" />
          <Text className="whitespace-nowrap text-sm font-normal text-slate-500">
            Recommended
          </Text>
        </Row>
      );
    }

    const isRowRecommending = isRecommending && recommendingJobId === jobId;

    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              className="whitespace-nowrap"
              disabled={isRecommending}
              onClick={() => {
                handleRecommend(job);
              }}
              size="sm"
              variant="outline"
            >
              {isRowRecommending ? (
                <LoadingIndicator variant="small" />
              ) : (
                "Recommend now"
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">
            <Text className="text-xs font-normal text-slate-900">
              Staffer will be notified and candidate added to applied
            </Text>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const renderRow = (job: RecommendedJob, index: number) => {
    const agenticScoreBucket = agenticScoreToBucket(job.agenticScore);
    const stafferIds = job.candidacyOwnerRotation?.rotation ?? [];

    return (
      <TableRow
        className={cn(job.company?.id && job.id && "cursor-pointer")}
        key={job.id ?? `${job.title ?? "job"}-${String(index)}`}
        onClick={() => {
          handleRowClick(job);
        }}
      >
        <TableCell>
          <Column className="min-w-0 gap-0.5">
            {job.id && job.company?.id ? (
              <RecommendedJobTitlePreview
                companyId={job.company.id}
                jobId={job.id}
                title={job.title ?? "Untitled job"}
              />
            ) : (
              <Text className="truncate font-medium text-slate-900">
                {job.title ?? "Untitled job"}
              </Text>
            )}
            <Text className="truncate text-xs font-normal text-slate-500">
              {job.company?.name ?? "—"}
            </Text>
          </Column>
        </TableCell>
        <TableCell>
          <PriorityCell
            job={{ id: job.id, priority: job.priority ?? null }}
            readOnly
          />
        </TableCell>
        {hasAgenticScore ? (
          <TableCell>
            {agenticScoreBucket ? (
              <AgenticScoreChip bucket={agenticScoreBucket} variant="xs" />
            ) : (
              <EmptyDataDash />
            )}
          </TableCell>
        ) : null}
        <TableCell>
          <StafferCell job={{ stafferIds }} />
        </TableCell>
        <TableCell>
          {job.estimatedRevenueAmount === null ||
          job.estimatedRevenueAmount === undefined ? (
            <EmptyDataDash />
          ) : (
            <Text className="whitespace-nowrap text-sm font-normal text-[#121212] dark:text-[#FAFAFA]">
              {formatting.currency(job.estimatedRevenueAmount)}
            </Text>
          )}
        </TableCell>
        {/* Recommend action must not trigger the row's open-job click. */}
        <TableCell
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          {renderRecommendCell(job)}
        </TableCell>
      </TableRow>
    );
  };

  const renderEmptyRow = () => {
    if (isSearching) {
      return (
        <TableRow>
          <TableCell className="p-0" colSpan={columnCount}>
            <Column className="items-center gap-2 py-8">
              <SearchIcon className="h-5 w-5 text-slate-400" />
              <Text className="text-sm font-normal text-slate-500">
                No open jobs match your search.
              </Text>
            </Column>
          </TableCell>
        </TableRow>
      );
    }
    return (
      <TableRow>
        <TableCell className="p-0" colSpan={columnCount}>
          <Column className="items-center gap-2 py-8">
            <BotIcon className="h-5 w-5 text-slate-400" />
            <Text className="text-sm font-normal text-slate-500">
              No recommended jobs found for this candidate within 50 miles.
            </Text>
          </Column>
        </TableCell>
      </TableRow>
    );
  };

  const renderLoadingRow = () => {
    return (
      <TableRow>
        <TableCell className="p-0" colSpan={columnCount}>
          <Column className="items-center py-8">
            <LoadingIndicator variant="medium" />
          </Column>
        </TableCell>
      </TableRow>
    );
  };

  const renderBody = () => {
    // The search reads the companies summary, which may still be loading the
    // first time the recruiter types.
    if (isSearching && companiesSummaryQuery.isPending) {
      return renderLoadingRow();
    }
    if (displayedJobs.length === 0) return renderEmptyRow();
    return displayedJobs.map(renderRow);
  };

  const render = () => {
    if (recommendedJobsQuery.isPending) {
      return (
        <Column className={cn("mt-6", contentPaddingClassName)}>
          <LoadingIndicator variant="medium" />
        </Column>
      );
    }
    if (recommendedJobsQuery.isError) {
      return (
        <Column className={cn("mt-6", contentPaddingClassName)}>
          <ErrorText errorMessage="Error retrieving recommended jobs." />
        </Column>
      );
    }

    return (
      <Column className={cn("gap-3 py-7", contentPaddingClassName)}>
        <ClearableSearchInput
          onChange={setSearchQuery}
          placeholder="Search for a job that would be a good fit."
          value={searchQuery}
        />
        <div className="min-w-0 overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <Table className="min-w-[900px] text-sm">
            <TableHeader className="bg-slate-50 [&_th]:border-b [&_th]:border-slate-200">
              <TableRow>
                {renderHeader("Jobs", "min-w-[220px]")}
                {renderHeader("Priority")}
                {hasAgenticScore ? renderHeader("Fit for role") : null}
                {renderHeader("Staffer")}
                {renderHeader("Rev. per req")}
                {renderHeader("Recommend")}
              </TableRow>
            </TableHeader>
            <TableBody className="[&_td]:!px-3 [&_td]:!py-3 [&_tr:last-child_td]:border-b-0 [&_tr_td]:border-b [&_tr_td]:border-slate-100">
              {renderBody()}
            </TableBody>
          </Table>
        </div>
        {previewJob?.id && previewJob.company?.id && previewJob.company.name ? (
          <RecommendedJobPreviewDialog
            companyId={previewJob.company.id}
            companyName={previewJob.company.name}
            jobId={previewJob.id}
            onOpenChange={(isOpen) => {
              if (!isOpen) setPreviewJob(undefined);
            }}
          />
        ) : null}
      </Column>
    );
  };

  return render();
};
